/* eslint-disable no-alert */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-restricted-globals */
import 'tippy.js/dist/tippy.css';
import 'select2/dist/css/select2.css';

import $ from 'jquery';
import tippy from 'tippy.js';
import { isUndefined } from 'lodash-es';
import { applyNamingConvention } from './utils/naming-conventions';
import testSome from './utils/testSome';

class FormElementFactory {
    constructor(ElementClass, selector, ignoreSelector) {
        this.ElementClass = ElementClass;
        this.selector = selector || ElementClass.selector;
        this.ignoreSelector = ignoreSelector || ElementClass.ignoreSelector;
    }

    createElements(form) {
        const elements = form.formElement.find(this.selector).not(this.ignoreSelector).get();
        return elements.map((element) => new this.ElementClass($(element)), this);
    }
}

class FormElement {
    constructor(element) {
        this.init(element);
        this.name = this.resolveName();
        this.topRight = this.createTopRightBox();
    }

    static createFactory(selector, ignoreSelector) {
        return new FormElementFactory(this, selector, ignoreSelector);
    }
}

export class FormField extends FormElement {
    static selector = ':input';

    // Ignore inputs without ID's.
    static ignoreSelector = '*:not([id]), button';

    init(element) {
        this.input = element;
        this.container = element.closest('li');

        // Shortcut for getting and setting the value.
        this.val = $.fn.val.bind(this.input);
    }

    resolveName() {
        if (this.input.prop('name')) {
            return this.input.prop('name');
        }
        if (this.input.prop('id')) {
            // Strip `id_` part of element id.
            const id = this.input.prop('id');
            return id.replace(/^id_/gi, '');
        }
        throw new Error('Could not resolve name of form field.');
    }

    createTopRightBox() {
        // Create the top right box that contains the override checkbox and info icon.
        const element = $('<div class="top-right">');
        this.container.append(element);
        return element;
    }

    onValueChange(func) {
        this.input.change(() => {
            func(this.val());
        });
        return this;
    }
}

export class FormFieldGroup extends FormElement {
    static selector = '> div';

    static ignoreSelector = '.form-actions, .form-errors';

    init(element) {
        this.container = element;
    }

    resolveName() {
        if (this.container.prop('id')) {
            return this.container.prop('id');
        }
        throw new Error('Could not resolve name of form field group.');
    }

    createTopRightBox() {
        // Create the top right box that contains the override checkbox and info icon.
        const element = $('<div class="top-right">');
        this.container.find('> :header').append(element);
        return element;
    }
}

/**
 * Initializes a Form object.
 *
 * @param {JQuery} element - The form element from this form.
 * @param {FormElementFactory[]} selectors - Optional array of FormElementFactory instances.
 */
export class Form {
    constructor(element, selectors) {
        function createDefaultSelectorSet() {
            return [FormField.createFactory()];
        }
        this.formElement = element;
        this.elementSelectors = selectors || createDefaultSelectorSet();
        this.elements = [];
        this.createElements();
    }

    createElements() {
        this.elementSelectors.forEach((selector) => {
            Array.prototype.push.apply(this.elements, selector.createElements(this));
        }, this);
    }

    /**
     * @param {FormElement} type - Optional type to be filtered on.
     */
    getElements(type) {
        if (!type) {
            // We do not have to filter, so just return everything.
            return this.elements;
        }
        return this.elements.filter((element) => element instanceof type);
    }

    /**
     * @param {string} name - Optional type to be filtered on.
     * @param {FormElement} type - Optional type to be filtered on.
     */
    getFormElement(name, type) {
        const groupPrefix = '__group__';
        if (name.startsWith(groupPrefix)) {
            return this.getFormElement(name.replace(groupPrefix, ''), FormFieldGroup);
        }
        return this.getElements(type).find((element) => element.name === name);
    }

    getField(name) {
        const field = this.getFormElement(name, FormField);

        if (!field) {
            throw Error(`Field ${name} not found.`);
        }
        return field;
    }
}

export class ProjectOverrides {
    constructor(form, overriddenFields, project, multiFields) {
        this.form = form;
        // These are the current fields that are overloaded according to the database.
        this.overriddenFields = overriddenFields;
        this.ignoreFields = [];
        this.multiFields = multiFields || [];
        this.project_loaded = false;
        if (project) {
            this.updateProjectData(project);
        }
        this.override_warning = true;
    }

    getFields(selector = '*') {
        function isNotIgnoredAndSelect(field) {
            return !testSome(this.ignoreFields, field.name) && field.input.is(selector);
        }
        return this.form.getElements(FormField).filter(isNotIgnoredAndSelect, this);
    }

    emitProjectUpdatedEvent() {
        window.dispatchEvent(new Event('projectUpdated'));
    }

    override() {
        // Override fields and create checkboxes and warning popups.
        if (this.project_loaded) {
            this.undo();
        }
        this.overrideFields(this.project);
        this.applyProjectSettingOptions(this.project);
        this.applyPopUps();
        this.applyCheckboxes();
        this.project_loaded = true;
        // Set the project name so that selenium tests know when the override is done.
        window.project_name = this.project.name;
        this.emitProjectUpdatedEvent();
    }

    undo() {
        // Undo the project overrides. Go back to a clean form.
        this.removeProjectSettingOptions();
        this.removeCheckboxes();
        this.removePopUps();
        this.project_loaded = false;
    }

    applyCheckboxes() {
        // Create checkboxes that will disable the fields.
        this.getFields("input:not([name*='_time_'], [type='submit']), textarea:visible").forEach(
            (field) => {
                this.createCheckbox(field);
                field.input
                    .wrap(
                        $("<div class='project-override-container'></div>")
                            .hover(
                                () => {
                                    field.checkbox[0].tippy.show();
                                },
                                () => {
                                    field.checkbox[0].tippy.hide();
                                }
                            )
                            .click(() => {
                                if (!field.checkbox[0].checked) {
                                    if (this.override_warning) {
                                        if (
                                            confirm(
                                                gettext(
                                                    'Are you sure you want to override the project settings?'
                                                )
                                            )
                                        ) {
                                            this.override_warning = false;
                                        } else {
                                            return;
                                        }
                                    }
                                    field.checkbox[0].click();
                                }
                            })
                    )
                    .after($(`<span class='project-override'></span>`));
                field.checkbox.on('click', this.toggleField.bind(this, field));
                this.toggleField(field);
            }
        );

        this.getFields('textarea:hidden').forEach((field) => {
            this.createCheckbox(field);
            field.container
                .find("input:not([type='checkbox'])")
                .wrap(
                    $("<div class='project-override-container'></div>").hover(
                        () => {
                            field.checkbox[0].tippy.show();
                        },
                        () => {
                            field.checkbox[0].tippy.hide();
                        }
                    )
                )
                .after($(`<span class='project-override'></span>`));
            field.checkbox.on('click', this.toggleField.bind(this, field));
            this.toggleField(field);
        });
        // Create invisible checkboxes that change based on selected input.
        this.getFields('select').forEach((field) => {
            this.createCheckbox(field);
            field.checkbox.hide();
            field.container.find('select').change((input) => {
                field.checkbox.prop(
                    'checked',
                    !$(input.target)
                        .find(':selected')
                        .text()
                        .endsWith(` - ${gettext('project setting')}`)
                );
            });
        });
    }

    applyPopUps() {
        this.getFields('select').forEach(this.createPopUp, this);
    }

    removePopUps() {
        this.getFields('select').forEach(this.removePopUp, this);
    }

    overrideFields(data) {
        this.getFields().forEach((field) => {
            if (!this.overriddenFields.includes(field.name)) {
                field.input.val(data[field.name]);
                field.input.trigger('change');
            }
        });
        for (const field of this.multiFields) {
            field.refresh(false);
        }
    }

    updateProjectData(data) {
        this.getFields().forEach((field) => {
            if (!(field.name in data) || data[field.name] === null) {
                data[field.name] = null;
            } else if (field.name === 'alarm_recipients') {
                data[field.name] = JSON.stringify(data[field.name]);
            } else if (data[field.name] === false) {
                data[field.name] = 'False';
            } else if (data[field.name] === true) {
                data[field.name] = 'True';
            } else {
                data[field.name] = String(data[field.name]);
            }
        });
        for (let i = 0; i < 7; i++) {
            if (
                data[`enable_time_${i}`] === '00:00:00' &&
                data[`disable_time_${i}`] === '23:59:00'
            ) {
                data[`enabled_day_${i}`] = '1';
            } else if (
                data[`enable_time_${i}`] === '00:00:00' &&
                data[`disable_time_${i}`] === '00:00:00'
            ) {
                data[`enabled_day_${i}`] = '2';
            } else {
                data[`enabled_day_${i}`] = '3';
            }
        }
        this.project = data;
    }

    createCheckbox(field) {
        const checkId = `override-${field.input.prop('id')}`;
        const checked = this.overriddenFields.includes(field.name);

        // Add the checkbox to the fields.
        const checkbox = $("<input type='checkbox' class='project-override'>");
        checkbox.prop('name', checkId);
        checkbox.prop('id', checkId);
        checkbox.prop('checked', checked);
        this.createTippy(checkbox, gettext('Check to override project settings.'), '');
        checkbox.hover(
            () => {
                checkbox[0].tippy.show();
            },
            () => {
                checkbox[0].tippy.hide();
            }
        );

        field.topRight.append(checkbox);
        field.checkbox = checkbox;
        field.container.data('checkbox', checkbox);
    }

    createPopUp(field) {
        this.createTippy(
            field.container,
            gettext('Note: changing this value will override the project settings.'),
            'focusin'
        );
    }

    createTippy(field, text, trigger) {
        field.each((index, element) => {
            element.tippy = tippy(element, {
                content: text,
                popperOptions: {
                    modifiers: {
                        name: 'preventOverflow',
                    },
                },
                trigger,
                theme: 'omnidots-white-shadow',
                allowHTML: true,
            });
        });
    }

    removePopUp(field) {
        field.container.each((index, element) => {
            element.tippy.destroy();
        });
    }

    applyProjectSettingOptions(project) {
        this.getFields().forEach((field) => {
            if ($(field.container).is(':visible')) {
                const selectBox = field.container.find('select');
                const originalValue = selectBox.val();
                const selected = !this.overriddenFields.includes(field.name);
                const originalOption = field.container
                    .find('select')
                    .not('.project-override')
                    .find('option')
                    .filter(function filter() {
                        return $(this).val() === project[field.name];
                    });

                if (originalOption.length) {
                    const projectOption = originalOption
                        .clone()
                        .appendTo(originalOption.parent())
                        .text(`${originalOption.text()} - ${gettext('project setting')}`)
                        // Needed for schedule selection.
                        .prop('selected', selected);

                    // Add the `publicName` property that the config helper uses to distinguish
                    // this option.
                    // Unfortunately we need to add it manually as jQuery `clone` does not copy
                    // custom properties.
                    const publicName = originalOption.prop('publicName');
                    if (!isUndefined(publicName)) {
                        projectOption.prop('publicName', publicName);
                    }
                }

                if (!selected) {
                    // Without this settings can drop to the first entry in the selection.
                    selectBox.val(originalValue);
                }
            }
        });
    }

    removeProjectSettingOptions() {
        $(`option:contains(" - ${gettext('project setting')}")`).remove();
    }

    toggleField(field) {
        const isDisabled = !field.checkbox.prop('checked');
        this.multiFields.forEach((multiField) => {
            if (field.input.get(0) === multiField.textarea.get(0)) {
                multiField.refresh(isDisabled, isDisabled);
            }
        });

        field.container
            .find('input, textarea, select')
            .not('.project-override')
            .prop('disabled', isDisabled);
        if (isDisabled) {
            field.input.val(this.project[field.name]);
        }
    }

    enableFields(field) {
        field.container
            .find('input, textarea, select')
            .not('.project-override')
            .prop('disabled', false);
    }

    removeCheckboxes() {
        this.getFields().forEach((field) => {
            field.checkbox && field.checkbox.remove();
            this.enableFields(field);
        }, this);
        $('span.project-override').remove();
    }
}

export class FormInformation {
    constructor(form, information) {
        this.form = form;
        this.information = information;
        this.iconHtml = '<i class="fa fa-info-circle info-icon"></i>';
    }

    applyInfoIcons() {
        Object.entries(this.information).forEach(([name, information]) => {
            const object = this.form.getFormElement(name);
            if (!object) {
                throw new Error(`Field with name '${name}' not found.`);
            }
            const icon = $(this.iconHtml);
            object.tippy = tippy(icon.get(0), {
                onShow(instance) {
                    instance.setContent(applyNamingConvention(information));
                },
                popperOptions: {
                    modifiers: {
                        name: 'preventOverflow',
                    },
                },
                theme: 'omnidots-white-shadow',
                allowHTML: true,
            });
            object.topRight.append(icon);
        });
    }
}
